import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { useAuth } from "@/hooks/use-auth";
import { useStatus } from "@/hooks/use-status";
import { Toaster } from "@/components/ui/sonner";

import { LoginPage } from "@/pages/login";
import { UsersPage } from "@/pages/users";
import { CreateUserPage } from "@/pages/create-user";
import { EditUserPage } from "@/pages/edit-user";
import { ChangePasswordPage } from "@/pages/change-password";
import { BulkImportPage } from "@/pages/bulk-import";
import { StatsPage } from "@/pages/stats";
import { ProfilesPage } from "@/pages/profiles";
import { ProfileDownloadPage } from "@/pages/profile-download";
import { AccessDeniedPage } from "@/pages/access-denied";
import { NotFoundPage } from "@/pages/not-found";

function AuthenticatedLayout() {
  const { user, isLoading, logout } = useAuth();
  const { data: status } = useStatus();
  const location = useLocation();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <AppShell
      username={user.username}
      onLogout={handleLogout}
      vpnStatus={status?.status}
    >
      <Outlet />
    </AppShell>
  );
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <>
      <Toaster position="top-center" theme="light" richColors />
      <Routes>
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route path="/access-denied" element={<AccessDeniedPage />} />
        <Route path="/p/:token" element={<ProfileDownloadPage />} />
        <Route element={<AuthenticatedLayout />}>
          <Route index element={<UsersPage />} />
          <Route path="users/create" element={<CreateUserPage />} />
          <Route path="users/:id/edit" element={<EditUserPage />} />
          <Route path="users/:id/password" element={<ChangePasswordPage />} />
          <Route path="bulk-import" element={<BulkImportPage />} />
          <Route path="stats" element={<StatsPage />} />
          <Route path="profiles" element={<ProfilesPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
